import type { ComponentProps, ReactElement } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

type NativeInputProps = Omit<
  ComponentProps<typeof Input>,
  'name' | 'value' | 'onChange' | 'onBlur' | 'ref'
>;
export interface FormTextInputProps<TFieldValues extends FieldValues>
  extends NativeInputProps {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  description?: string;
  className?: string;
  labelClassName?: string;
  nullable?: boolean;
}
export const FormTextInput = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  description,
  className,
  labelClassName,
  nullable = false,
  type,
  ...inputProps
}: FormTextInputProps<TFieldValues>): ReactElement => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem className={className}>
        <FormLabel
          className={cn(
            'text-xs font-medium uppercase text-muted-foreground',
            labelClassName
          )}>
          {label}
        </FormLabel>
        <FormControl>
          <Input
            type={type}
            {...inputProps}
            {...field}
            value={field.value ?? ''}
            onChange={(event) => {
              if (type === 'number') {
                const numericValue = event.target.valueAsNumber;
                field.onChange(
                  Number.isNaN(numericValue) ? undefined : numericValue
                );
              } else if (nullable && event.target.value.trim() === '') {
                field.onChange(undefined);
              } else {
                field.onChange(event.target.value);
              }
            }}
          />
        </FormControl>
        {description !== undefined && (
          <FormDescription className='text-xs'>{description}</FormDescription>
        )}
        <FormMessage />
      </FormItem>
    )}
  />
);
